/** Shared help copy — used on Profile “Get Help” and Help Center (Talabat-style topics). */

export interface HelpTopic {
  id: string;
  group: string;
  title: string;
  summary: string;
  details: string[];
}

export const HELP_CONTACT = {
  email: 'support@qahwahub.com',
  feedbackEmail: 'feedback@qahwahub.com',
  phone: 'xxx-xxx-xxxx',
  phoneDisplay: 'xxx-xxx-xxxx',
  hours: '24 hours, 7 days a week',
};

export const helpTopics: HelpTopic[] = [
  {
    id: 'order-status',
    group: 'Orders',
    title: 'Check my order status',
    summary: 'See if your order was received and is being prepared',
    details: [
      'Open My Orders from the bottom menu or the link below.',
      'Each order shows its current status (pending, preparing, ready, completed, and more).',
      'You will get a push notification when the café updates your order (if notifications are enabled).',
    ],
  },
  {
    id: 'order-change',
    group: 'Orders',
    title: 'Change or cancel my order',
    summary: 'Add, remove, or cancel before the café starts preparing',
    details: [
      'Contact the café as soon as possible if you need to change items.',
      'You can cancel from the order screen while the status is still pending or received.',
      'Once preparation has started, cancellation may not be possible.',
    ],
  },
  {
    id: 'order-wrong',
    group: 'Orders',
    title: 'Wrong or missing items',
    summary: 'Report an issue with what you received',
    details: [
      'Open My Orders, find the completed order, and tap Report an issue (within 48 hours of pickup).',
      'Add photos and a short description, then submit.',
      'After submitting, tap View report status on that order anytime to see review progress.',
      'Reviews usually take 24–48 hours. For urgent help, email support@qahwahub.com with your order number.',
    ],
  },
  {
    id: 'order-issue-track',
    group: 'Orders',
    title: 'Track my order issue report',
    summary: 'See whether your refund or quality report is still in review',
    details: [
      'Go to My Orders from the bottom menu.',
      'On a completed order where you already reported a problem, tap View report status.',
      'Status shows steps like Submitted, Under review, Approved, or Declined. Pull down on the list to refresh.',
      'Card refunds after approval can take 3–7 business days. Email support@qahwahub.com with your order number if you need help sooner.',
    ],
  },
  {
    id: 'order-late',
    group: 'Orders',
    title: 'My order is late',
    summary: 'Pickup or preparation is taking longer than expected',
    details: [
      'Busy periods can increase wait times at the bar.',
      'Check the latest status in My Orders — it updates when the barista moves your order forward.',
      'If it is significantly late, contact support with your order number.',
    ],
  },
  {
    id: 'order-reorder',
    group: 'Orders',
    title: 'Order again (re-order)',
    summary: 'Repeat a previous order from history',
    details: [
      'Go to My Orders and open a completed order.',
      'Use the same items to order again from that café when available.',
    ],
  },
  {
    id: 'payment-methods',
    group: 'Payment',
    title: 'Payment methods & charges',
    summary: 'Visa, cash, and what you pay on QahwaHub',
    details: [
      'We accept Visa and cash at participating cafés.',
      'You pay when you place the order (or at pickup for cash, depending on the café).',
      'Premium subscription is 2.50 JOD per month, shown on your Profile before you confirm payment.',
      'There is no extra QahwaHub service fee beyond your order total and any café delivery rules.',
    ],
  },
  {
    id: 'payment-refund',
    group: 'Payment',
    title: 'Refunds & payment problems',
    summary: 'Card declined, double charge, or refund timing',
    details: [
      'If a card payment failed, try again or use cash if the café supports it.',
      'For refunds on cancelled or failed orders, timing depends on your bank (often several business days).',
      'For a completed order, open My Orders → select the order → Report an issue (photo required, within 48 hours).',
      'Email support with your order number and payment reference for fastest help.',
    ],
  },
  {
    id: 'order-issue-report',
    group: 'Payment',
    title: 'Report a problem with a delivered order',
    summary: 'Missing items, wrong order, quality, or payment issues',
    details: [
      'Go to My Orders and open a completed order, then tap Report an issue.',
      'Choose what went wrong, select affected items if needed, describe the problem, and upload at least one photo.',
      'Reports must be submitted within 48 hours of pickup. One report per order.',
      'We review within 24–48 hours; card refunds may take 3–7 business days.',
    ],
  },
  {
    id: 'points-rewards',
    group: 'Points & Premium',
    title: 'Points & free drinks',
    summary: 'Earn, redeem, and Premium 2× points',
    details: [
      'Earn 1 point per 1 JOD spent (2 points per JOD with Premium).',
      'Redeem 50 points for one free drink up to the cheapest drink price on that order.',
      'Points and redemptions appear under Profile → Points & Rewards and Points History.',
    ],
  },
  {
    id: 'premium',
    group: 'Points & Premium',
    title: 'Premium subscription',
    summary: 'Benefits, fee, and cancellation',
    details: [
      'Premium costs 2.50 JOD per month (shown on Profile before you confirm payment).',
      'Benefits include 2× points on orders and exclusive offers when available.',
      'Cancel anytime from Profile → Subscription.',
    ],
  },
  {
    id: 'account-login',
    group: 'Account',
    title: 'Login & account access',
    summary: 'Sign in, guest checkout, or locked account',
    details: [
      'Use the email and password you registered with.',
      'Guest checkout creates a temporary session so you can still place orders.',
      'If you entered the wrong password many times, wait a few minutes and try again, or reset via support.',
    ],
  },
  {
    id: 'app-technical',
    group: 'App',
    title: 'App not working',
    summary: 'Loading, map, or notification issues',
    details: [
      'Check your internet connection and try closing and reopening the app.',
      'Allow location permission if cafés or the map do not appear.',
      'Enable notifications in device settings for order updates.',
    ],
  },
  {
    id: 'contact-general',
    group: 'Contact',
    title: 'Talk to customer care',
    summary: 'Live chat, phone, email, and feedback',
    details: [
      `Phone (${HELP_CONTACT.hours}): ${HELP_CONTACT.phoneDisplay}`,
      `Email: ${HELP_CONTACT.email}`,
      `Feedback: ${HELP_CONTACT.feedbackEmail}`,
      'For order issues, include your order number and café name.',
    ],
  },
];

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

export const helpFaqs: FAQItem[] = [
  {
    id: '1',
    category: 'General',
    question: 'What is QahwaHub?',
    answer:
      'QahwaHub connects you with partner cafés in Jordan. Browse menus, place orders for pickup, earn points, and manage everything from one app.',
  },
  {
    id: '2',
    category: 'Orders',
    question: 'How do I place an order?',
    answer:
      'Choose a café, add drinks and food to your cart, then checkout with Visa or cash. You can order as a guest or with a registered account.',
  },
  {
    id: '3',
    category: 'Orders',
    question: 'How do I know my order was placed?',
    answer:
      'Go to My Orders. If your order appears there, it was received. Watch the status for updates from the café.',
  },
  {
    id: '4',
    category: 'Orders',
    question: 'Can I modify or cancel my order?',
    answer:
      'Cancel from the order page while status is pending or received. For changes, contact the café quickly before preparation starts.',
  },
  {
    id: '5',
    category: 'Orders',
    question: 'Can I re-order a previous order?',
    answer: 'Open a past order under My Orders and order the same items again when the café is available.',
  },
  {
    id: '6',
    category: 'Orders',
    question: 'I was not happy with my order — who do I contact?',
    answer: `Contact us at ${HELP_CONTACT.phoneDisplay} or ${HELP_CONTACT.email} with your order number. We will follow up with the café.`,
  },
  {
    id: '7',
    category: 'Payment',
    question: 'What payment methods are accepted?',
    answer: 'Visa and cash. Payment is collected when you place your order according to the method you choose.',
  },
  {
    id: '8',
    category: 'Payment',
    question: 'Can I use points to pay for orders?',
    answer:
      'With 400+ points you can redeem for a free drink (up to the cheapest drink on that order). Remaining balance is paid by card or cash.',
  },
  {
    id: '9',
    category: 'Points & Rewards',
    question: 'How do I earn points?',
    answer: '1 point per 1 JOD spent. Premium members earn 2 points per JOD.',
  },
  {
    id: '10',
    category: 'Points & Rewards',
    question: 'How do I redeem points?',
    answer: 'At checkout, if you have 400+ points, redemption applies automatically when eligible.',
  },
  {
    id: '11',
    category: 'Subscription',
    question: 'What is Premium?',
    answer: 'Premium (2.50 JOD / month) gives 2× points on orders. Manage it under Profile → Subscription.',
  },
  {
    id: '12',
    category: 'Subscription',
    question: 'How do I cancel Premium?',
    answer: 'Profile → Subscription → Cancel Subscription.',
  },
  {
    id: '13',
    category: 'Technical',
    question: 'The app is not loading properly',
    answer: 'Check your connection, restart the app, and ensure you are on the latest version.',
  },
  {
    id: '14',
    category: 'Technical',
    question: "I can't see cafés on the map",
    answer: 'Enable location permissions for QahwaHub in your device settings.',
  },
  {
    id: '15',
    category: 'General',
    question: 'How can I contact QahwaHub?',
    answer: `Call ${HELP_CONTACT.phoneDisplay} (${HELP_CONTACT.hours}), email ${HELP_CONTACT.email}, or send feedback to ${HELP_CONTACT.feedbackEmail}.`,
  },
];

export const helpFaqCategories = [
  'All',
  'General',
  'Orders',
  'Payment',
  'Points & Rewards',
  'Subscription',
  'Account',
  'Technical',
];

export const helpTopicGroups = ['Orders', 'Payment', 'Points & Premium', 'Account', 'App', 'Contact'];

/** Profile → Get Help: payment topics + contact only */
export const profileHelpTopicGroups = ['Payment', 'Contact'];

export const profileHelpTopics = helpTopics.filter((t) =>
  profileHelpTopicGroups.includes(t.group)
);
